import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '../../../../components/PageTitle';
import { Box, Chip } from '@mui/material';
import { useBranding } from '../../../../hooks/api/useBranding';
import { usePersistentSorting } from '../../../../hooks/usePersistentSorting';
import {
  AgreementIsActive,
  Agreements,
} from '../../../../types/Agreements.types';
import { AgreementsTableViewProps } from '../../Agreements.types';
import { AgreementsFilters } from './AgreementsFilters';
import { AgreementsListHeader } from './AgreementsListHeader';
import { AgreementsTableDesktop } from './AgreementsTableDesktop';
import { AgreementsTableMobile } from './AgreementsTableMobile';
import styled from '@emotion/styled';
import { statusColors } from './StatusColors';

export const StyledStatusChip = styled(Chip)<{
  status: AgreementIsActive | undefined;
}>`
  && {
    background-color: ${(props) =>
      props.status ? statusColors[props.status] : 'transparent'};
    text-transform: capitalize;
  }
`;

export const AgreementsList = ({
  agreements,
  journals,
}: AgreementsTableViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const repo_url = branding?.repo_url;
  const { order, orderBy, setOrder, setOrderBy } =
    usePersistentSorting<Agreements>('_id', 'agreements');

  const [repository, setRepository] = useState<string>('');
  const [expiringSoonFilter, setExpiringSoonFilter] = useState(false);
  const [publisherFilter, setPublisherFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<AgreementIsActive[]>([]);

  const handleOpen = (agreementId: string) => {
    navigate(`/agreements/${agreementId}`);
  };

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof Agreements
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    if (repo_url) {
      setRepository(repo_url);
    }
  }, [repo_url]);

  const agreementsWithJournals = useMemo(() => {
    if (!agreements || !journals) return [];

    return agreements.map((agreement) => ({
      ...agreement,
      journals: journals.filter(
        (journal) => journal.agreement_id === agreement._id
      ),
    }));
  }, [agreements, journals]);

  const activeAgreements = () => {
    if (!agreementsWithJournals) {
      return [];
    }
    return agreementsWithJournals.filter(
      (x) => x.computed_status === AgreementIsActive.active
    );
  };

  const activeJournalsCount = () => {
    const agreementsActive = activeAgreements();
    if (agreementsActive.length === 0) {
      return 0;
    }
    return agreementsActive
      .map((x) => x.journals_count)
      .reduce((a, b) => {
        return a! + b!;
      });
  };

  const sortedAgreements = useMemo(() => {
    if (!agreementsWithJournals) return [];

    return [...agreementsWithJournals].sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA! > valueB!) return order === 'asc' ? 1 : -1;
      if (valueA! < valueB!) return order === 'asc' ? -1 : 1;
      return 0;
    });
  }, [agreementsWithJournals, order, orderBy]);

  const filteredAgreements = useMemo(() => {
    let result = sortedAgreements;

    if (expiringSoonFilter) {
      const now = new Date();
      const sixtyDaysFromNow = new Date(
        now.getTime() + 60 * 24 * 60 * 60 * 1000
      );
      result = result.filter((agreement) => {
        if (!agreement.end_date) return false;
        const endDate = new Date(agreement.end_date);
        return endDate >= now && endDate <= sixtyDaysFromNow;
      });
    }

    if (publisherFilter.trim()) {
      const search = publisherFilter.toLowerCase().trim();
      result = result.filter((agreement) =>
        agreement.publisher?.toLowerCase().includes(search)
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter(
        (agreement) =>
          agreement.computed_status &&
          statusFilter.includes(agreement.computed_status)
      );
    }

    return result;
  }, [sortedAgreements, expiringSoonFilter, publisherFilter, statusFilter]);

  return (
    <>
      <PageTitle title={t('home.agreementsTitle') as string} />
      <AgreementsListHeader
        activeAgreementsCount={activeAgreements().length}
        activeJournalsCount={activeJournalsCount() ?? 0}
        repository={repository}
      />
      <AgreementsFilters
        publisherFilter={publisherFilter}
        onPublisherFilterChange={setPublisherFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        expiringSoonFilter={expiringSoonFilter}
        onExpiringSoonFilterChange={setExpiringSoonFilter}
      />
      <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 24 }}>
        <AgreementsTableDesktop
          agreements={filteredAgreements}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          onRowClick={handleOpen}
        />
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 24 }}>
        <AgreementsTableMobile
          agreements={filteredAgreements}
          onRowClick={handleOpen}
        />
      </Box>
    </>
  );
};
